/**
 * src/data/VisualRulesBank.js
 * Hardcoded scenario bank for Part 8: The Visual Rules.
 */
export const visualRulesBank = [
  {
    "id": 1,
    "text": "Wearing a full three-piece suit to a first-round interview at an early-stage, Series A tech startup.",
    "correctId": "A",
    "feedback": "Fail. Overdressing to this extreme signals you did zero research on the company culture and lack contextual adaptability."
  },
  {
    "id": 2,
    "text": "Joining a 15-minute internal daily standup from your phone while walking outside, with the camera stabilized.",
    "correctId": "B",
    "feedback": "Fit. For low-stakes internal syncs, demonstrating mobility and active listening is acceptable, provided the audio/video is stable."
  },
  {
    "id": 3,
    "text": "Blurring your background using Zoom's native filter because you are working from a visibly messy bedroom.",
    "correctId": "B",
    "feedback": "Fit. Strategic neutrality. A blurred background removes visual friction and protects your professional boundary when your physical environment is compromised."
  },
  {
    "id": 4,
    "text": "Using a highly animated, looping virtual background (e.g., a tropical beach with moving waves) during a Quarterly Business Review.",
    "correctId": "A",
    "feedback": "Fail. Distracting visual noise. Animated backgrounds draw cognitive focus away from the data being presented and diminish executive presence."
  },
  {
    "id": 5,
    "text": "Wearing a crisp, unbranded black t-shirt to a cross-functional sync with the engineering team.",
    "correctId": "B",
    "feedback": "Fit. Matches the room's energy. A clean, neutral t-shirt aligns perfectly with standard tech-industry operational attire without appearing sloppy."
  },
  {
    "id": 6,
    "text": "Attending an executive board presentation from a dark room, lit entirely by the blue glow of your computer monitor.",
    "correctId": "A",
    "feedback": "Fail. Poor lighting inherently reduces subconscious trust and makes you appear unprepared or secretive. Always front-light your face."
  },
  {
    "id": 7,
    "text": "Wearing a branded company polo shirt during a high-stakes external vendor negotiation.",
    "correctId": "B",
    "feedback": "Fit. Professional framing. Branded apparel projects institutional authority and aligns you visibly as an official representative of the enterprise."
  },
  {
    "id": 8,
    "text": "Having a massive, unmade bed prominently visible over your left shoulder during a 1:1 with your Director.",
    "correctId": "A",
    "feedback": "Fail. Poor framing and boundary violation. Exposing an unkempt intimate space to leadership signals a lack of situational awareness and organizational hygiene."
  },
  {
    "id": 9,
    "text": "Logging into a client kickoff with the laptop resting flat on your lap, angling the camera sharply upward toward your ceiling.",
    "correctId": "A",
    "feedback": "Fail. Unprofessional framing. The 'nostril cam' angle distorts your proportions and forces the client to look down on you, subtly shifting power dynamics."
  },
  {
    "id": 10,
    "text": "Layering a sharp, structured blazer over a clean, neutral hoodie for a hybrid creative-agency pitch.",
    "correctId": "B",
    "feedback": "Fit. Contextual adaptability. This hybrid aesthetic threads the needle perfectly between formal authority and modern creative culture."
  },
  {
    "id": 11,
    "text": "Having a highly controversial political poster prominently framed in your background during a team all-hands meeting.",
    "correctId": "A",
    "feedback": "Fail. Polarizing visual noise. Injecting personal political ideology into a neutral corporate environment creates unnecessary friction and alienates colleagues."
  },
  {
    "id": 12,
    "text": "Joining a troubleshooting call with your camera off to preserve dropping bandwidth, but immediately stating the reason in the chat.",
    "correctId": "B",
    "feedback": "Fit. Operational awareness. Prioritizing audio stability over video during a technical issue is the correct triage, and communicating the 'why' maintains etiquette."
  },
  {
    "id": 13,
    "text": "Leaving your camera off during an intimate, 4-person brainstorming session where every other participant has their camera on.",
    "correctId": "A",
    "feedback": "Fail. Failure to read the room. In small collaborative settings, opting out of visual engagement when peers are vulnerable creates a profound trust deficit."
  },
  {
    "id": 14,
    "text": "Positioning your desk so your background is a plain, solid-color wall for a high-stakes investor pitch.",
    "correctId": "B",
    "feedback": "Fit. Strategic neutrality. A blank wall forces the audience's entire cognitive focus onto your face and your message, rather than your environment."
  },
  {
    "id": 15,
    "text": "Eating a full, messy meal on camera while a colleague is presenting a dense slide deck.",
    "correctId": "A",
    "feedback": "Fail. Distracting visual noise and poor etiquette. Eating on camera during someone else's presentation is highly distracting and signals a lack of respect for their effort."
  },
  {
    "id": 16,
    "text": "Looking directly into the physical camera lens—rather than at the faces on your screen—while delivering your closing argument to a client.",
    "correctId": "B",
    "feedback": "Fit. Professional framing. Staring into the lens simulates direct, sustained eye contact for the recipient, dramatically increasing the perceived confidence of your pitch."
  },
  {
    "id": 17,
    "text": "Wearing a formal tuxedo to a Friday afternoon internal virtual happy hour.",
    "correctId": "A",
    "feedback": "Fail. Contextual misfire. While humorous in a vacuum, severely overdressing for a casual team-building event can make others feel uncomfortable and ruins the intended relaxed vibe."
  },
  {
    "id": 18,
    "text": "Logging into an urgent 1:1 with your manager from the driver's seat of your parked car because your home Wi-Fi unexpectedly crashed.",
    "correctId": "B",
    "feedback": "Fit. Pragmatic triage. In an operational emergency, demonstrating problem-solving by finding a quiet, private, and stable environment (even a parked car) outweighs aesthetic perfection."
  },
  {
    "id": 19,
    "text": "Positioning your camera so your head is at the very bottom of the frame, leaving 80% of the screen as empty ceiling space.",
    "correctId": "A",
    "feedback": "Fail. Poor framing. Excessive headroom diminishes your executive presence, making you appear physically smaller and less authoritative in the meeting."
  },
  {
    "id": 20,
    "text": "Wearing a crisp button-down shirt, but leaving the top three buttons undone, exposing visible chest hair during a vendor negotiation.",
    "correctId": "A",
    "feedback": "Fail. Boundary violation. While collared shirts are standard, excessive unbuttoning signals a lack of professional restraint and distracts from your negotiation leverage."
  },
  {
    "id": 21,
    "text": "Using a highly stylized ring light that reflects two perfectly visible, glowing white circles in your glasses during a client pitch.",
    "correctId": "A",
    "feedback": "Fail. Distracting technical artifact. The constant glare prevents the client from seeing your eyes, which subconsciously damages interpersonal trust and creates visual fatigue."
  },
  {
    "id": 22,
    "text": "Wearing a high-end luxury designer logo tee (e.g., Gucci, Balenciaga) to your annual compensation review meeting.",
    "correctId": "A",
    "feedback": "Fail. Tone-deaf signaling. Wearing ostentatious luxury branding while simultaneously asking for a salary increase demonstrates a profound lack of contextual calibration."
  },
  {
    "id": 23,
    "text": "Sipping coffee from a clean, neutral ceramic mug while listening during an early morning cross-functional sync.",
    "correctId": "B",
    "feedback": "Fit. Human but unobtrusive. Consuming a standard beverage in a neutral vessel is perfectly acceptable and does not break the room's visual focus."
  },
  {
    "id": 24,
    "text": "Drinking from a massive, 64-ounce neon motivational water jug during an executive briefing.",
    "correctId": "A",
    "feedback": "Fail. Visual clutter. Oversized, brightly colored gym accessories dragged into a boardroom environment pull focus from the data being presented."
  },
  {
    "id": 25,
    "text": "Having a towering stack of disorganized Amazon delivery boxes visible in the background during a client onboarding call.",
    "correctId": "A",
    "feedback": "Fail. Organizational hygiene failure. A chaotic background subconsciously signals to the client that your workflow and project management will be equally disorganized."
  },
  {
    "id": 26,
    "text": "Sitting in front of a heavily curated, color-coordinated bookshelf for a thought-leadership webinar.",
    "correctId": "B",
    "feedback": "Fit. Projects intellectual authority. A clean, intentionally designed physical background enhances credibility and functions as excellent personal branding for public speaking."
  },
  {
    "id": 27,
    "text": "Having a roommate repeatedly walking back and forth in the background of your shot during a confidential legal review.",
    "correctId": "A",
    "feedback": "Fail. Security and privacy risk. Failing to secure a private visual environment during a confidential meeting compromises enterprise data and shows poor spatial awareness."
  },
  {
    "id": 28,
    "text": "Using a tasteful, static, high-resolution image of a modern office interior as your virtual background while working from a loud coffee shop.",
    "correctId": "B",
    "feedback": "Fit. Strategic masking. When your physical environment is uncontrollable, deploying a clean, professional virtual background establishes visual privacy and eliminates background friction."
  },
  {
    "id": 29,
    "text": "Attending a team meeting in a dimly lit room with your face entirely silhouetted by a bright, unshaded window behind you.",
    "correctId": "A",
    "feedback": "Fail. Witness-protection framing. Backlighting completely obscures your micro-expressions, rendering you a faceless shadow and severely limiting your ability to communicate effectively."
  },
  {
    "id": 30,
    "text": "Wearing a sharp blazer over a collared shirt, but shifting constantly in a squeaky, brightly colored racing-style gaming chair.",
    "correctId": "A",
    "feedback": "Fail. Aesthetic clash. The extreme contrast between formal business attire and neon gamer hardware creates a jarring visual disconnect that undermines your professional framing."
  },
  {
    "id": 31,
    "text": "Using a subtle, warm desk lamp to illuminate your face on a dark, overcast day to maintain visual clarity.",
    "correctId": "B",
    "feedback": "Fit. Technical hygiene. Proactively managing your lighting to ensure you are clearly visible demonstrates respect for the medium and the people on the other end of the call."
  },
  {
    "id": 32,
    "text": "Showing up to an internal 'Ugly Sweater Holiday Party' wearing a stiff, traditional corporate suit because you 'don't do costumes.'",
    "correctId": "A",
    "feedback": "Fail. Cultural rigidity. Failing to temporarily adjust your visual footprint for a sanctioned team-building event signals that you take yourself too seriously and resist group cohesion."
  },
  {
    "id": 33,
    "text": "Joining a remote call with your camera elevated to eye level, keeping your head and shoulders centered in the top two-thirds of the frame.",
    "correctId": "B",
    "feedback": "Fit. Optimal framing. This mimics natural, in-person posture and eye contact, establishing an authoritative, engaged, and highly professional digital presence."
  },
  {
    "id": 34,
    "text": "Wearing heavily tinted blue-light blocking glasses that completely obscure your eyes during a high-stakes performance review.",
    "correctId": "A",
    "feedback": "Fail. Micro-expression block. In high-stakes emotional negotiations, eye contact is critical. Tinted or highly reflective lenses break this connection and breed subconscious mistrust."
  },
  {
    "id": 35,
    "text": "Wearing a highly visible, ostentatious luxury watch while delivering a cost-cutting presentation to a financially struggling client.",
    "correctId": "A",
    "feedback": "Fail. Tone-deaf signaling. Displaying extreme personal wealth while discussing financial hardship destroys empathy and creates a massive psychological disconnect with the client."
  },
  {
    "id": 36,
    "text": "Leaving your camera on but pointing it squarely at the ceiling because you are eating during a massive 500-person town hall.",
    "correctId": "A",
    "feedback": "Fail. Environmental negligence. If you cannot be on camera, turn the software video off. Broadcasting a live feed of your ceiling is visually sloppy and draws unnecessary attention."
  },
  {
    "id": 37,
    "text": "Wearing a tailored, neutral-colored zip-up fleece vest over a button-down to a meeting with a Silicon Valley venture capital firm.",
    "correctId": "B",
    "feedback": "Fit. Cultural chameleon. This specific aesthetic is the unofficial uniform of the tech/VC sector. Wearing it signals immediate in-group alignment and cultural fluency."
  },
  {
    "id": 38,
    "text": "Using a virtual background without a physical green screen, causing your hair and ears to constantly glitch and disappear into the digital background.",
    "correctId": "A",
    "feedback": "Fail. Technical artifacting. The constant flickering of a struggling background algorithm creates severe visual fatigue for the viewer and makes your feed look cheap."
  },
  {
    "id": 39,
    "text": "Positioning a secondary monitor entirely off-camera, requiring you to turn your head 90 degrees away from the lens every time you read your notes.",
    "correctId": "A",
    "feedback": "Fail. Broken eye contact. Constantly showing the side of your head signals distraction and breaks the parasocial connection required for effective remote communication."
  },
  {
    "id": 40,
    "text": "Maintaining a neutral resting facial expression and nodding slowly during a 45-minute monologue by a senior executive, rather than forcing a constant smile.",
    "correctId": "B",
    "feedback": "Fit. Professional pacing. Forcing a permanent smile for 45 minutes looks artificial and manic. Neutral, active listening is the optimal posture for long-form absorption."
  },
  {
    "id": 41,
    "text": "Using an ultra-wide angle (fisheye) webcam lens that heavily distorts the edges of your room and bows the walls during a client introduction.",
    "correctId": "A",
    "feedback": "Fail. Optical distortion. Fisheye lenses warp proportions and create a dizzying, unnatural viewing experience that undermines executive framing."
  },
  {
    "id": 42,
    "text": "Logging into a strictly internal, casual Friday team sync wearing a clean, unbranded baseball cap to cover a bad hair day.",
    "correctId": "B",
    "feedback": "Fit. Contextual triage. In a highly casual, internal-only setting, a neat hat is an acceptable, pragmatic solution to maintain visual hygiene without over-engineering."
  },
  {
    "id": 43,
    "text": "Setting your camera angle so a bright, flashing neon sign outside your window is directly visible behind your head.",
    "correctId": "A",
    "feedback": "Fail. High-contrast kinetic noise. Bright, moving light sources in the background forcibly pull the viewer's eye away from your face, hijacking their cognitive focus."
  },
  {
    "id": 44,
    "text": "Using a standing desk and shifting your weight slightly from side to side during a long internal strategy session.",
    "correctId": "B",
    "feedback": "Fit. Kinetic energy management. Standing promotes better diaphragmatic breathing and vocal projection, and minor natural shifting is perfectly acceptable in long meetings."
  },
  {
    "id": 45,
    "text": "Working from a coffee shop and positioning yourself so the busy ordering counter and line of customers is directly behind you.",
    "correctId": "A",
    "feedback": "Fail. High-traffic background. Exposing your camera to unpredictable public foot traffic creates massive visual chaos and severe security/privacy risks."
  },
  {
    "id": 46,
    "text": "Wearing a crisp, white dress shirt while sitting against a completely white wall under bright lighting.",
    "correctId": "A",
    "feedback": "Fail. The 'floating head' effect. Failing to create visual contrast between your wardrobe and your environment makes the feed look washed out and highly distracting."
  },
  {
    "id": 47,
    "text": "Stacking your laptop on several books to elevate the webcam precisely to your eye level while traveling in a bare hotel room.",
    "correctId": "B",
    "feedback": "Fit. Resourceful environmental control. Hacking your physical setup on the road to maintain professional framing demonstrates a commitment to high visual standards."
  },
  {
    "id": 48,
    "text": "Keeping your camera on during a 1:1, but audibly typing on a mechanical keyboard while staring at a different screen every time your manager speaks.",
    "correctId": "A",
    "feedback": "Fail. Multi-tasking optics. Even if you are taking meeting notes, the visual combination of typing and looking away signals that you are answering emails and ignoring the speaker."
  },
  {
    "id": 49,
    "text": "Bringing a visible mug of tea into a morning client sync, but only taking sips when the client is speaking or transitioning slides.",
    "correctId": "B",
    "feedback": "Fit. Strategic pacing. Hydrating is human, but timing your sips for when you do not hold the floor minimizes visual disruption and maintains your momentum as a speaker."
  },
  {
    "id": 50,
    "text": "Using a virtual background of a massive, opulent mansion library when you are a junior analyst.",
    "correctId": "A",
    "feedback": "Fail. Illusory environmental flex. Deploying an obviously fake, hyper-luxurious background creates a jarring psychological disconnect and makes you appear insecure and inauthentic."
  }
];